import ArrowRightAlt from '@mui/icons-material/ArrowRightAlt';
import Box from '@mui/material/Box';
import { API } from 'aws-amplify';
import { orderBy } from 'lodash';
import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AdContext } from '../contexts/ads-context';
import { AppContext } from '../contexts/app-context';
import { AuthContext } from '../contexts/auth-context';
import { useSessionActions } from '../hooks/use-session-actions';
import { CarrierHeaderLink } from '../types';
import { captureError } from '../utils/capture-error';
import { APP_BAR } from './alliance/alliance-tokens';
import {
  AppBanners,
  ConnectionBanner,
  MaintenanceBanner,
  UpdatedBanner,
} from './alliance/app-banners';
import { AppNavDrawer, SIDEBAR_WIDTH } from './alliance/app-nav-drawer';
import { AppShellBar } from './app-shell-bar';
import { BannerCarousel } from './banner-carousel';
import { navData, NavDataLink, NavDataSection } from './navigation/nav-data';
import { NavDrawerBody } from './navigation/nav-drawer';
import { Notifications } from './notifications';
import { SessionExpiredDialog } from './alliance/session-expired-dialog';
import { Popups } from './pop-ups';
import { RouteErrorBoundary } from './route-container/route-error-boundary';

/**
 * The signed-in shell, top to bottom: the ad banner carousel (fixed, not on
 * /admin), the App Bar under it, the 280px sidebar (permanent from md, a
 * temporary drawer below), and the page outlet. The banner's measured height
 * (AdContext.bannerHeight) offsets the bar, the permanent sidebar and the
 * content.
 *
 * Under the bar, the banner strips (maintenance for admins, connection
 * trouble, a new build, billing setup). The outlet sits in a route error
 * boundary (inline crash, or "Dashboard has been updated" for a failed lazy
 * chunk) and is keyed so "Try again" on a page that didn't load remounts it.
 *
 * Also mounts the notifications drawer, pop-ups and the session-expired
 * dialog, and adds the `/carriers/header` carriers to the Carriers nav.
 *
 * @param maintenanceEnd - Set while maintenance is active for an admin; shows
 *   the admins-only maintenance banner
 */
export function Dashboard(props: { maintenanceEnd?: string | null }) {
  // Context
  const { state, dispatch } = React.useContext(AppContext);
  const authContext = React.useContext(AuthContext);
  const adContext = React.useContext(AdContext);
  // Ref
  const bannerRef = React.useRef<HTMLDivElement>(null);
  // State
  const [data, setData] = React.useState<NavDataSection[]>([]);
  const [bannerHeight, setBannerHeight] = React.useState(0);
  // Hooks
  const location = useLocation();
  const { signOut, signInAgain } = useSessionActions();
  const isAdminPath = /^\/admin(\/|$)/.test(location.pathname);
  const closeDrawer = React.useCallback(
    () => dispatch({ type: 'DRAWER_CLOSE' }),
    [dispatch],
  );

  React.useEffect(() => {
    // Close the drawer once a link has been followed
    closeDrawer();
  }, [location.pathname, closeDrawer]);

  React.useEffect(() => {
    if (!bannerRef.current) {
      adContext.dispatch({
        type: 'SET_BANNER_HEIGHT',
        payload: { bannerHeight: 0 },
      });
      setBannerHeight(0);
      return;
    }

    const updateHeight = () => {
      const newHeight = isAdminPath ? 0 : bannerRef.current?.offsetHeight || 0;
      setBannerHeight(newHeight);
      adContext.dispatch({
        type: 'SET_BANNER_HEIGHT',
        payload: { bannerHeight: newHeight },
      });
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(bannerRef.current);

    return () => resizeObserver.disconnect();
    // eslint-disable-next-line
  }, [isAdminPath]);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response: {
          data: CarrierHeaderLink[];
        } = await API.post('DashboardAPI', '/carriers/header', { body: {} });

        // Filter out children of parent carriers
        const dataFiltered = response.data
          .filter((i) => !i.CarrierCode.startsWith('ap-'))
          .filter((i) => !i.CarrierCode.startsWith('ma-'))
          .filter((i) => !i.CarrierCode.startsWith('ms-'));

        // Order data by Title
        const dataOrdered = orderBy(dataFiltered, 'Title');

        // Format data to match NavDataLink
        const newLinks: NavDataLink[] = [];
        dataOrdered.forEach((i) => {
          newLinks.push({
            title: i.Title,
            to: `/carriers/${i.CarrierCode}`,
            startIcon: <ArrowRightAlt fontSize="small" color="primary" />,
          });
        });

        // Update navigation data
        setData((currentState) =>
          currentState.map((section) => {
            if (section.title === 'Carriers') {
              return {
                ...section,
                links: [...section.links, ...newLinks],
              };
            } else {
              return section;
            }
          }),
        );
      } catch (error) {
        captureError({ data: { error } });
      }
    };

    setData(navData);
    fetchData();
  }, []);
  return (
    <Box sx={{ display: 'flex' }}>
      <Notifications />
      <Popups />
      <SessionExpiredDialog
        open={authContext.state.tokenError}
        onSignIn={signInAgain}
        onSignOut={signOut}
      />

      {/* Banner sources: each keeps its banner in the queue while it applies */}
      <ConnectionBanner />
      <UpdatedBanner pathname={location.pathname} />
      {props.maintenanceEnd ? (
        <MaintenanceBanner until={props.maintenanceEnd} onSignOut={signOut} />
      ) : null}
      {!isAdminPath ? (
        <Box
          ref={bannerRef}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.appBar + 1,
          }}
        >
          <BannerCarousel />
        </Box>
      ) : null}

      <AppShellBar />

      <AppNavDrawer
        open={state.drawerOpen}
        onClose={closeDrawer}
        topOffset={bannerHeight}
      >
        <NavDrawerBody data={data} />
      </AppNavDrawer>

      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: { md: `calc(100% - ${SIDEBAR_WIDTH}px)` },
          minHeight: '100vh',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          // Clear the fixed ad banner and App Bar
          pt: {
            xs: `${bannerHeight + APP_BAR.heightMobile}px`,
            md: `${bannerHeight + APP_BAR.heightDesktop}px`,
          },
        }}
      >
        <AppBanners />

        <RouteErrorBoundary>
          <React.Fragment key={state.routeKey}>
            <Outlet />
          </React.Fragment>
        </RouteErrorBoundary>
      </Box>
    </Box>
  );
}
